import { VOXELS_PER_METRE, WORLD_HEIGHT } from "../sim/world/constants";
import {
  COAST_BLEND_END,
  EDGE_CLIFF_THICKNESS,
  OCEAN_SHELF_WIDTH,
  SEA_LEVEL,
  SURFACE_EROSION,
  WORLD_EXTENT,
  voxels,
} from "./constants";
import { alphaRegionGraph, biomeWeightsAt, regionAt } from "./regions";
import { fractalNoise2, smoothstep, valueNoise2 } from "./random";
import type { BiomeId, RegionGraph, TerrainSample } from "./types";

interface TerrainProfile {
  readonly base: number;
  readonly relief: number;
  readonly detail: number;
}

/**
 * Profiles are authored in **metres above sea level**; heights are stored in
 * voxels. Anchoring to `SEA_LEVEL` rather than to y=0 is what keeps a beach a
 * beach across the micro-voxel rescale — the constant moved from 62 m to 64 m,
 * and an absolute base would have put the whole coast under water.
 */
function profile(baseMetres: number, reliefMetres: number, detailMetres: number): TerrainProfile {
  return Object.freeze({
    base: SEA_LEVEL + baseMetres * VOXELS_PER_METRE,
    relief: reliefMetres * VOXELS_PER_METRE,
    detail: detailMetres * VOXELS_PER_METRE,
  });
}

/** Deeply immutable profiles for the full biome vocabulary; Alpha selects three. */
/*
 * Relief was far too small to be the world the references show.
 *
 * Measured, the whole 1 km map spanned 15.5 m from its lowest point to its
 * highest — a putting green. Nothing was ever steep enough for the slope
 * classifier to call it rock, so the rock face in `vibeshot 01-54-38` had
 * nothing to be built from. Forest and plains now carry real hills.
 *
 * The gradient still lands well inside MAX_NATURAL_TERRAIN_STEP: the broad
 * octave is 22 m over a 180 m wavelength and the detail 4 m over 19 m, so
 * around one voxel of rise per voxel travelled against a limit of four.
 */
export const BIOME_TERRAIN: Readonly<Record<BiomeId, TerrainProfile>> = Object.freeze({
  forest: profile(11, 21, 4),
  plains: profile(7, 10, 2.2),
  coast: profile(1, 4, 1.2),
  mountains: profile(29, 28, 5),
  swamp: profile(2, 2, 1),
  desert: profile(8, 8, 3),
  tundra: profile(10, 7, 2),
  badlands: profile(16, 16, 4),
  volcanic: profile(22, 23, 5),
  highlands: profile(20, 18, 4),
  marsh: profile(2, 2, 0.8),
  "ancient-woods": profile(11, 12, 3),
});

/** Noise is sampled in metres so wavelengths survive the micro-voxel rescale. */
function metres(value: number): number {
  return value / VOXELS_PER_METRE;
}

/**
 * Rivers are the zero-crossing of one broad noise field: a single winding line
 * across the map rather than a network, which is all the references show and
 * all the slice needs. The turquoise ribbon is a major colour anchor — without
 * it a forest reads as a wall of green.
 */
const RIVER_WAVELENGTH_METRES = 240;
/** Half-width of the channel in noise units; the band is |field| < this. */
const RIVER_BANK = 0.028;
/** Depth of the cut and the drop from bank to water surface, in metres. */
const RIVER_DEPTH = voxels(2.4);
const RIVER_FREEBOARD = voxels(0.5);
/** No rivers in the coastal blend: they would just drain into the shelf. */
const RIVER_MIN_ELEVATION = voxels(2.5);

/** Where the shelf meets the shore: one metre under water, as before the rescale. */
const SHORE_HEIGHT = SEA_LEVEL - voxels(1);
/** Deepest point of the ocean shelf, at the world's outer edge. */
const SHELF_FLOOR = SEA_LEVEL - voxels(27);

/** Highest and lowest surface the natural (non-cliff) generator can produce. */
export const MAX_NATURAL_HEIGHT = Math.ceil(
  Math.max(...Object.values(BIOME_TERRAIN).map(({ base, relief, detail }) => base + relief + detail)),
);
export const MIN_NATURAL_HEIGHT = Math.floor(Math.min(
  SHELF_FLOOR,
  ...Object.values(BIOME_TERRAIN).map(({ base, relief, detail }) => base - relief - detail),
));

function clampWorldCoordinate(value: number): number {
  return Math.max(0, Math.min(WORLD_EXTENT - 1, value));
}

export function distanceFromWorldEdge(x: number, z: number): number {
  return Math.min(x, z, WORLD_EXTENT - 1 - x, WORLD_EXTENT - 1 - z);
}

/**
 * Ridged noise: sharp crests where the field crosses zero, smooth troughs
 * between them.
 *
 * Raising the smooth octaves alone gave a 27 m map that was still a putting
 * green — the relief was spread over hundreds of metres, so the steepest ground
 * anywhere rose 50 voxels across 240 and no slope ever reached the threshold
 * for rock. Bluffs need short-wavelength *creases*, which is exactly what
 * folding the noise about zero produces.
 */
/**
 * Ridge amplitudes and wavelengths, in metres.
 *
 * **Slope is amplitude over wavelength**, and that ratio is the only thing that
 * decides whether a world has cliffs. At 12.6 m of ridge spread over 46 m,
 * measurement said 85% of the map lay under 27 degrees and only 1% passed 45:
 * rolling downland with no face anywhere you could not stroll up, whatever the
 * total relief was. Making the map taller did nothing, twice.
 *
 * The steep octave is therefore about as tall as it is wide. The ceiling is
 * MAX_NATURAL_TERRAIN_STEP — four voxels between adjacent columns, so that
 * every natural slope stays inside the player's own step height — and this
 * lands near half of it.
 */
const RIDGE_BROAD_WAVELENGTH = 76;
const RIDGE_BROAD_AMPLITUDE = 15;
const RIDGE_STEEP_WAVELENGTH = 13;
const RIDGE_STEEP_AMPLITUDE = 10;

/** Broad landform shape, 0 to 1, for deciding where high ground sits. */
function ridgedNoise(seed: number, x: number, z: number): number {
  return 1 - Math.abs(valueNoise2(
    seed,
    metres(x) / RIDGE_BROAD_WAVELENGTH,
    metres(z) / RIDGE_BROAD_WAVELENGTH,
  ));
}

/** The crag octave: as tall as it is wide, which is what makes a face. */
function cragNoise(seed: number, x: number, z: number): number {
  return 1 - Math.abs(valueNoise2(
    seed,
    metres(x) / RIDGE_STEEP_WAVELENGTH,
    metres(z) / RIDGE_STEEP_WAVELENGTH,
  ));
}

function inlandHeight(seed: number, x: number, z: number, graph: RegionGraph): number {
  const broad = fractalNoise2(seed ^ 0x73a53f19, metres(x), metres(z));
  const detail = valueNoise2(seed ^ 0x13d4a7c5, metres(x) / 19, metres(z) / 19);
  const ridge = ridgedNoise(seed ^ 0x0a5b_3d71, x, z);
  const crag = cragNoise(seed ^ 0x5d3a_11c7, x, z);
  // Crags only break out on the high shoulders, so valleys and meadows stay
  // soft and the drama is where the land is already rising.
  const cragMask = smoothstep(0.42, 0.78, ridge);
  let height = 0;
  for (const { biome, weight } of biomeWeightsAt(graph, x, z)) {
    const profile = BIOME_TERRAIN[biome];
    height += (profile.base
      + broad * profile.relief
      + detail * profile.detail
      + ridge * voxels(RIDGE_BROAD_AMPLITUDE)
      + crag * cragMask * voxels(RIDGE_STEEP_AMPLITUDE)) * weight;
  }

  return height;
}

/** Continuous surface sampling shared by chunks and prefab conformance. */
export function sampleTerrain(seed: number, x: number, z: number, graph = alphaRegionGraph(seed)): TerrainSample {
  const worldX = clampWorldCoordinate(x);
  const worldZ = clampWorldCoordinate(z);
  const edgeDistance = distanceFromWorldEdge(worldX, worldZ);
  const nearest = regionAt(graph, worldX, worldZ);
  const weights = biomeWeightsAt(graph, worldX, worldZ);
  const edgeCliff = edgeDistance < EDGE_CLIFF_THICKNESS;
  let continuousHeight: number;
  if (edgeCliff) {
    continuousHeight = WORLD_HEIGHT - 1;
  } else if (edgeDistance < OCEAN_SHELF_WIDTH) {
    // The ramp already meets SHORE_HEIGHT at the shelf's inner edge, exactly
    // where the coast branch starts from SHORE_HEIGHT — but the noise does not
    // vanish there, so it lands as a hard step the full width of its amplitude.
    // Taper it to zero at the shore and the two branches join continuously.
    //
    // This was invisible at one-metre blocks: the amplitude is 1.5 m, which was
    // 1.5 units and slipped under MAX_NATURAL_TERRAIN_STEP. At eight voxels per
    // metre the same 1.5 m is 12 units, and the seam breaks the invariant.
    const shelfNoise = valueNoise2(seed ^ 0x61c88647, metres(worldX) / 32, metres(worldZ) / 32) * voxels(1.5);
    const shoreTaper = 1 - edgeDistance / OCEAN_SHELF_WIDTH;
    continuousHeight = SHELF_FLOOR
      + edgeDistance * ((SHORE_HEIGHT - SHELF_FLOOR) / OCEAN_SHELF_WIDTH)
      + shelfNoise * shoreTaper;
  } else {
    const inland = inlandHeight(seed, worldX, worldZ, graph);
    const coastAmount = smoothstep(OCEAN_SHELF_WIDTH, COAST_BLEND_END, edgeDistance);
    continuousHeight = SHORE_HEIGHT + (inland - SHORE_HEIGHT) * coastAmount;
  }
  // Rivers cut the height field itself rather than being painted on top, so
  // every reader — vegetation, the slope classifier, the sun march — sees the
  // channel without being told about it.
  let river = 0;
  let riverSurface = 0;
  if (!edgeCliff) {
    // fractalNoise2 applies its own 1/180 frequency, so its input is already in
    // metres — dividing again put the whole 1 km world inside a fraction of one
    // period and the field came out constant, with no zero crossing anywhere.
    const meanderScale = 180 / RIVER_WAVELENGTH_METRES;
    const meander = fractalNoise2(
      seed ^ 0x3f1a_77c9,
      metres(worldX) * meanderScale,
      metres(worldZ) * meanderScale,
    );
    // Both limits fade rather than switch. A hard guard on elevation or on the
    // coast band makes the whole carve vanish the instant it fails, which is a
    // cliff the depth of the river and breaks the terrain-continuity invariant
    // — 26 voxels between adjacent columns, where the limit is 4.
    const elevationFade = smoothstep(
      SEA_LEVEL + RIVER_MIN_ELEVATION,
      SEA_LEVEL + RIVER_MIN_ELEVATION + voxels(8),
      continuousHeight,
    );
    const coastFade = smoothstep(OCEAN_SHELF_WIDTH, COAST_BLEND_END, edgeDistance);
    river = (1 - smoothstep(0, RIVER_BANK, Math.abs(meander))) * elevationFade * coastFade;
    if (river > 0) {
      // Water sits just below the un-cut bank, so its surface follows the
      // terrain smoothly instead of needing a flow solve.
      riverSurface = Math.round(continuousHeight) - RIVER_FREEBOARD;
      continuousHeight -= RIVER_DEPTH * river;
    }
  }

  return Object.freeze({
    height: Math.max(1, Math.min(WORLD_HEIGHT - 1, Math.round(continuousHeight))),
    river,
    riverSurface,
    biome: edgeDistance < COAST_BLEND_END ? "coast" : nearest.biome,
    biomeWeights: weights,
    edgeDistance,
    oceanShelf: !edgeCliff && edgeDistance < OCEAN_SHELF_WIDTH && continuousHeight < SEA_LEVEL,
    edgeCliff,
  });
}

export function terrainHeight(seed: number, x: number, z: number, graph?: RegionGraph): number {
  return sampleTerrain(seed, x, z, graph).height;
}

/**
 * Per-column displacement of the *written* surface, in voxels.
 *
 * `sampleTerrain().height` is a smooth quantised field, and a broad hill built
 * straight from it renders as concentric terraces — one flat ring per voxel of
 * elevation. Displacing each column by a couple of voxels of coherent noise
 * breaks those contours into an eroded bank (docs/ART-DIRECTION.md §0.2).
 *
 * Sampled at a ~14-voxel wavelength — nearly two metres — so the ground reads
 * as gently uneven rather than as a field of steps. A short wavelength against
 * the same amplitude puts a four-voxel cliff every five voxels, which is what
 * the meadow looked like before: chaotic rubble instead of a bank. The
 * references keep the ground broadly smooth and put the visual interest in the
 * grass standing on it.
 *
 * The perimeter cliff and anything at or below the waterline stay clean.
 *
 * Exported because every writer of surface-anchored geometry has to agree on
 * it. Terrain fill and tree roots using different ground is how trees end up
 * floating or buried.
 */
export function surfaceErosionAt(seed: number, x: number, z: number, sample: TerrainSample): number {
  if (sample.edgeCliff || sample.oceanShelf || sample.height <= SEA_LEVEL) return 0;
  /*
   * Three octaves, and the finest one has to reach a whole voxel or it does
   * nothing at all.
   *
   * The output is rounded to a voxel, and that rounding is unforgiving: an
   * octave whose amplitude is well under 1 can only ever flip a column that was
   * already sitting near a .5 boundary, so it mottles the surface in patches
   * without ever breaking a *step*. With a broad octave at a 14-voxel wavelength
   * and a fine one at 0.55 voxels of amplitude, that is exactly what happened —
   * every slope came out as a parallel run of dead-regular single-voxel treads,
   * one per voxel of fall, tens of voxels long. Corduroy.
   *
   * Terraces are a contour-following artefact, so what breaks them is variation
   * at the spacing of the contours themselves: on a moderate slope that is every
   * two or three columns. Hence the finest octave sits at roughly a 2-voxel
   * wavelength with enough amplitude to move a column by one, which makes the
   * tread edges ragged column by column instead of merely displacing them.
   *
   * The old note here warned that a short wavelength at full amplitude gives
   * chaotic rubble rather than a bank, and it is right: at 0.85 voxels adjacent
   * columns differ by about one, i.e. twelve centimetres of gravelly unevenness.
   * Taking the fine octave up to SURFACE_EROSION would put half-metre cliffs
   * every few voxels.
   */
  const broad = valueNoise2(seed ^ 0x2c1b_3f57, x / 14, z / 14);
  const mid = valueNoise2(seed ^ 0x51a3_9d27, x / 5, z / 5);
  const fine = valueNoise2(seed ^ 0x6f2c_11b3, x / 1.9, z / 1.9);
  // The finer octaves are cosmetic roughness; the crags are structure. When they
  // stack against MAX_NATURAL_TERRAIN_STEP it is these that give way.
  return Math.round(broad * SURFACE_EROSION + mid * 1.1 + fine * 0.85);
}

/** The surface as actually built: smooth height plus erosion. */
export function erodedSurfaceHeight(seed: number, x: number, z: number, graph?: RegionGraph): number {
  const sample = sampleTerrain(seed, x, z, graph);
  return sample.height + surfaceErosionAt(seed, x, z, sample);
}
